import { Box, Card, Link, Typography } from '@customink/pigment-react';
import { useProduct } from '@customink/project-service-react-components';
import type { ReactEventHandler } from 'react';

import ProofImage from '@/components/ProofImage';
import type { Complement } from '@/types';
import { fireEvent, pageViewTag } from '@/utils/metrics';

const addProductButtonStyles = {
  whiteSpace: 'nowrap',
  '&:hover': {
    textDecoration: 'none',
  },
};

const complementCardStyles = {
  display: 'flex',
  flexDirection: 'column',
  cursor: 'pointer',
  containerType: 'inline-size', // Enable container queries
};

const contentStyles = {
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'column',
  flexGrow: '1',
  gap: '12px',
  padding: '10px 12px 12px', // default padding for narrow cards
  '@container (min-width: 180px)': {
    padding: '13px 16px 16px', // larger padding for wider cards
  },
};

type ComplementCardProps = {
  colorId: number;
  designCid: string;
  onClick: (complement: Complement) => void;
};
export default function ComplementCard({
  designCid,
  colorId,
  onClick,
}: ComplementCardProps) {
  const { data: product } = useProduct({
    colorId: colorId,
  });

  const selectedColor = product?.colors?.find((color) => color.id === colorId);

  if (!product || !selectedColor) return null;

  const complement = {
    colorId: colorId,
    colorName: selectedColor?.name,
    designCid: designCid,
    styleId: product.id,
    styleName: product.name,
  };

  const handleCardClick: ReactEventHandler = () => {
    pageViewTag('checkout/summary/addstyle');
    fireEvent('complementary styles', 'checkout summary', complement.styleName);
    onClick(complement);
  };

  // Don't allow adding blanks through the catalog iframe.
  return !complement.designCid ? null : (
    <Card
      data-testid="complement-card"
      onClick={handleCardClick}
      size="small"
      sx={complementCardStyles}
    >
      <ProofImage
        cid={complement.designCid}
        colorId={complement.colorId}
        view="front_large_extended.png"
        withLegacyOptions
      />
      <Box sx={contentStyles}>
        <Typography
          sx={{
            flexGrow: '1',
            display: '-webkit-box',
            WebkitLineClamp: 4,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            '@container (min-width: 160px)': {
              fontSize: '14px',
            },
          }}
          title={complement.styleName}
          variant="paragraphTiny"
        >
          {complement.styleName}
        </Typography>
        <Link sx={addProductButtonStyles} variant="uiTextSmall">
          Add this product
        </Link>
      </Box>
    </Card>
  );
}
